import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";

interface PlaceSuggestion {
  placeId: string;
  description: string;
}

interface AddressAutocompleteProps {
  value: string;
  onSelect: (address: string, coords?: { lat: number; lng: number }, zipCode?: string, city?: string, state?: string, streetAddress?: string, country?: string) => void;
  onBlurChange?: (address: string) => void;
  onChange?: (address: string) => void;
  placeholder?: string;
  disabled?: boolean;
  apiBase?: string;
  "data-testid"?: string;
}

export default function AddressAutocomplete({
  value,
  onSelect,
  onChange,
  onBlurChange,
  placeholder = "Full property address",
  disabled,
  apiBase = "/api/app",
  "data-testid": testId,
}: AddressAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value ?? "");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const updateDropdownPos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (showSuggestions) {
      updateDropdownPos();
      window.addEventListener("scroll", updateDropdownPos, true);
      window.addEventListener("resize", updateDropdownPos);
      return () => {
        window.removeEventListener("scroll", updateDropdownPos, true);
        window.removeEventListener("resize", updateDropdownPos);
      };
    }
  }, [showSuggestions, updateDropdownPos]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!input.trim() || input.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/places-autocomplete?input=${encodeURIComponent(input)}`, {
        signal: abortRef.current.signal,
        credentials: "include",
      });
      const data: PlaceSuggestion[] = await res.json();
      setSuggestions(data);
      setShowSuggestions(data.length > 0);
    } catch (e: any) {
      if (e.name !== "AbortError") {
        setSuggestions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const handleSelectSuggestion = useCallback(
    async (suggestion: PlaceSuggestion) => {
      setInputValue(suggestion.description);
      setSuggestions([]);
      setShowSuggestions(false);

      try {
        const res = await fetch(`${apiBase}/places-details?placeId=${encodeURIComponent(suggestion.placeId)}`, {
          credentials: "include",
        });
        const data = await res.json();

        if (data && !data.error) {
          let displayAddress = data.address || suggestion.description;
          if (data.zipCode && !displayAddress.includes(data.zipCode)) {
            displayAddress += `, ${data.zipCode}`;
          }
          setInputValue(displayAddress);
          const coords = data.lat != null && data.lng != null ? { lat: data.lat, lng: data.lng } : undefined;
          onSelect(displayAddress, coords, data.zipCode, data.city, data.state, data.streetAddress, data.country);
        } else {
          onSelect(suggestion.description);
        }
      } catch {
        onSelect(suggestion.description);
      }
    },
    [onSelect, apiBase]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (onChange) {
      onChange(val);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (onBlurChange) {
        onBlurChange(inputValue);
      }
    }, 200);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const dropdown =
    showSuggestions && suggestions.length > 0 && dropdownPos
      ? createPortal(
          <ul
            style={{
              position: "absolute",
              top: dropdownPos.top + 4,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 9999,
              pointerEvents: "auto",
            }}
            className="bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
            data-testid="address-suggestions"
            data-address-autocomplete-portal=""
          >
            {suggestions.map((s) => (
              <li
                key={s.placeId}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectSuggestion(s);
                }}
                data-testid={`suggestion-${s.placeId}`}
              >
                {s.description}
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        autoComplete="off"
      />
      {dropdown}
    </div>
  );
}
